
import React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Calendar, HelpCircle } from "lucide-react";
import type { MortgageData } from "../../CalculatorTabs";
import { AutoCalculateOptions } from "../../utils/types";

interface LoanTermButtonsProps {
  mortgageData: MortgageData;
  setMortgageData: React.Dispatch<React.SetStateAction<MortgageData>>;
  autoCalculate: AutoCalculateOptions;
  onLoanTermChange: (value: number) => void;
}

const LoanTermButtons: React.FC<LoanTermButtonsProps> = ({ 
  mortgageData, 
  setMortgageData, 
  autoCalculate, 
  onLoanTermChange 
}) => {
  const getAvailableTerms = () => {
    // Different loan types may have different typical terms
    switch (mortgageData.loanType) {
      case 'va':
        return [
          { value: 15, label: '15 Years', note: 'Lower total interest' },
          { value: 30, label: '30 Years', note: 'Lower monthly payment' }
        ];
      case 'fha':
        return [
          { value: 15, label: '15 Years', note: 'Lower total interest' },
          { value: 30, label: '30 Years', note: 'Most popular choice' }
        ];
      case 'usda':
        return [
          { value: 30, label: '30 Years', note: 'Standard USDA term' }
        ];
      case 'conventional':
      default:
        return [
          { value: 15, label: '15 Years', note: 'Pay off faster, save on interest' },
          { value: 20, label: '20 Years', note: 'Balance of payment and interest' },
          { value: 30, label: '30 Years', note: 'Lowest monthly payment' }
        ];
    }
  };

  const availableTerms = getAvailableTerms();
  
  const getTermImpact = (term: number) => {
    if (!mortgageData.homePrice || !mortgageData.interestRate) return null;
    
    const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
    const monthlyRate = mortgageData.interestRate / 100 / 12;
    const numPayments = term * 12;
    
    const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                          (Math.pow(1 + monthlyRate, numPayments) - 1);
    
    const totalPayment = monthlyPayment * numPayments;
    const totalInterest = totalPayment - loanAmount;
    
    return {
      monthlyPayment: monthlyPayment,
      totalInterest: totalInterest
    };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-medium">Loan Term</Label>
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <p className="text-sm">
                Longer terms mean lower monthly payments but more total interest paid over the life of the loan.
                Shorter terms have higher monthly payments but significant interest savings.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="flex flex-wrap gap-2">
        {availableTerms.map((term) => {
          const impact = getTermImpact(term.value);
          return (
            <TooltipProvider key={term.value}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant={mortgageData.loanTerm === term.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => onLoanTermChange(term.value)}
                    className="h-12 px-4 flex flex-col items-center"
                    debugLabel={`Loan Term ${term.value} Years`}
                  >
                    <span className="font-semibold">{term.value}</span>
                    <span className="text-xs opacity-75">years</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">{term.label}</p>
                    <p className="text-xs text-muted-foreground">{term.note}</p>
                    {impact && (
                      <div className="text-xs space-y-1 border-t pt-1">
                        <div>Monthly: {formatCurrency(impact.monthlyPayment)}</div>
                        <div>Total Interest: {formatCurrency(impact.totalInterest)}</div>
                      </div>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>

      {/* Term Comparison Display */}
      {mortgageData.loanTerm && (
        <div className="p-3 bg-muted/30 rounded-lg border">
          <div className="text-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">Selected: {mortgageData.loanTerm} Year Term</span>
              <span className="text-xs text-muted-foreground">
                {availableTerms.find(t => t.value === mortgageData.loanTerm)?.note}
              </span>
            </div>
            
            {(() => {
              const impact = getTermImpact(mortgageData.loanTerm);
              if (!impact) return null;
              
              return (
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="text-muted-foreground">Est. Monthly Payment:</span>
                    <div className="font-semibold">{formatCurrency(impact.monthlyPayment)}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Interest:</span>
                    <div className="font-semibold">{formatCurrency(impact.totalInterest)}</div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanTermButtons;
